import { useState, useEffect } from "react";
import { MdOutlineLocationOn } from "react-icons/md";
import { FiPhone, FiMail } from "react-icons/fi";
import { Address } from "../../footer";
import AOS from "aos";
import "aos/dist/aos.css";
import SEO from "../../../components/SEO";
import ContactForm from "./ContactForm";

const Contact = () => {
  const [src, setSrc] = useState(
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4351.808391807043!2d49.65635796524076!3d26.990845573863723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e35a1b7ec81aa69%3A0xfd3de2103a858495!2s101%20Business%20Tower!5e0!3m2!1sen!2sin!4v1731306676194!5m2!1sen!2sin"
  );

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const copyContactItems = (text, setText) => {
    navigator.clipboard.writeText(text);
    setText("Copied!");
    setTimeout(() => {
      setText(text);
    }, 2000);
  };

  const setAddressText = (text, setText) => {
    setText("Loading Map...");
    setTimeout(() => {
      setText(text);
    }, 2000);
  };

  document.title = `Krico | Contact`;

  return (
    <>
      <SEO 
        title="Contact Us | KRICO - Get in Touch for Electrical Solutions"
        description="Contact KRICO for expert electrical control panel solutions. Reach out to our team for inquiries, support, or collaboration opportunities. We're here to help with your industrial electrical needs."
        keywords="contact Krico, electrical panel support, industrial solutions contact, electrical engineering support, panel manufacturer contact"
        path="/contact"
      />
      <div className="py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-24 relative" data-aos="fade-up">
            <div className="absolute -top-10 left-0 w-24 h-24 bg-yellow-100 dark:bg-yellow-500/20 rounded-full blur-3xl"></div>
            <div className="mb-4 text-center">
              <span className="bg-yellow-50 dark:bg-yellow-500/30 text-yellow-600 dark:text-yellow-300 text-sm font-medium px-6 py-2 rounded-full border border-yellow-200/50 dark:border-yellow-500/30 shadow-sm">
                Get in Touch
              </span>
            </div>
            <h1 className="text-6xl font-bold text-slate-900 dark:text-yellow-300 mb-6 tracking-tight text-center">
              Contact Us
            </h1>
            <p className="text-2xl font-semibold text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed mx-auto text-center">
              We'd love to hear from you
            </p>
          </div>

          {/* Contact Form Section */}
          <div 
            className="mb-16"
            data-aos="fade-up"
          >
            <div className="p-8">
              <ContactForm />
            </div>
          </div>

          {/* Map and Contact Info Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Map Section */}
            <div 
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden backdrop-blur-lg border border-slate-200 dark:border-slate-700 h-[800px] transform transition-all hover:shadow-2xl"
              data-aos="fade-right"
            >
              <iframe
                src={src}
                title="google map"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </div>

            {/* Contact Information Section */}
            <div 
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden backdrop-blur-lg border border-slate-200 dark:border-slate-700 p-8 transform transition-all hover:shadow-2xl h-[800px]"
              data-aos="fade-left"
            >
              <div className="space-y-8">
                {/* Office Section */}
                <div className="transform transition-all hover:translate-x-2">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-yellow-300 mb-4 flex items-center">
                    <MdOutlineLocationOn className="w-6 h-6 mr-2 text-yellow-500 dark:text-yellow-400" />
                    Head Office
                  </h2>
                  <div className="text-slate-700 dark:text-slate-300">
                    <Address
                      addres={{
                        clk: (text, setText) => {
                          setSrc(
                            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4351.808391807043!2d49.65635796524076!3d26.990845573863723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e35a1b7ec81aa69%3A0xfd3de2103a858495!2s101%20Business%20Tower!5e0!3m2!1sen!2sin!4v1731306676194!5m2!1sen!2sin"
                          );
                          setAddressText(text, setText);
                        },
                        text: [
                          "KRICO PANNEL Global Private Limited,",
                          "#176 – Mettukadu, Chinnamedu,",
                          "Thindal, Erode – 638 012,",
                          "Tamilnadu, India.",
                        ],
                      }}
                      phone={{
                        clk: copyContactItems,
                        text: ["+91 97509 10064"],
                      }}
                      email={{
                        clk: copyContactItems,
                        text: ["sales@kricoelectric.com"],
                      }}
                      className="text-slate-700 dark:text-slate-300"
                    />
                  </div>
                </div>

                {/* Factory Section */}
                <div className="transform transition-all hover:translate-x-2">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-yellow-300 mb-4 flex items-center">
                    <FiMail className="w-6 h-6 mr-2 text-yellow-500 dark:text-yellow-400" />
                    Factory
                  </h2>
                  <div className="text-slate-700 dark:text-slate-300">
                    <Address
                      addres={{
                        clk: (text, setText) => {
                          setSrc(
                            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4351.808391807043!2d49.65635796524076!3d26.990845573863723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e35a1b7ec81aa69%3A0xfd3de2103a858495!2s101%20Business%20Tower!5e0!3m2!1sen!2sin!4v1731306676194!5m2!1sen!2sin"
                          );
                          setAddressText(text, setText);
                        },
                        text: [
                          "KRICO PANNEL Global Private Limited,",
                          "D.No.96/4, Thottipalayam Main Street,",
                          "Nasiyanur Post, Erode - 638 107",
                        ],
                      }}
                      phone={{
                        clk: copyContactItems,
                        text: ["+91 96889 10064"],
                      }}
                      email={{
                        clk: copyContactItems,
                        text: [
                          "kricoglobalpurchase21@gmail.com",
                        ],
                      }}
                      className="text-slate-700 dark:text-slate-300"
                    />
                  </div>
                </div>

                {/* Global Office Section */}
                <div className="transform transition-all hover:translate-x-2">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-yellow-300 mb-4 flex items-center">
                    <FiPhone className="w-6 h-6 mr-2 text-yellow-500 dark:text-yellow-400" />
                    Global Office
                  </h2>
                  <div className="text-slate-700 dark:text-slate-300">
                    <Address
                      addres={{
                        clk: (text, setText) => {
                          setSrc(
                            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4351.808391807043!2d49.65635796524076!3d26.990845573863723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e35a1b7ec81aa69%3A0xfd3de2103a858495!2s101%20Business%20Tower!5e0!3m2!1sen!2sin!4v1731306676194!5m2!1sen!2sin"
                          );
                          setAddressText(text, setText);
                        },
                        text: [
                          "KRICO PANNEL Global Private Limited,",
                          "101 Business Tower, Sharjah",
                          "United Arab Emirates",
                        ],
                      }}
                      phone={{
                        clk: copyContactItems,
                        text: ["+966 55 892 8453"],
                      }}
                      email={{
                        clk: copyContactItems,
                        text: ["admin@kricoelectric.com"],
                      }}
                      className="text-slate-700 dark:text-slate-300"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
